/**
 * Languages Management Page
 *
 * CRUD interface for managing languages with sorting, reordering, and status toggles.
 *
 * @package MultilingualPressZone
 */

import { el, __, clear, formatDate } from '../utils/dom.js';
import API from '../utils/api.js';
import Modal from '../components/Modal.js';
import { Button } from '../components/Button.js';
import { Toggle, ToggleSwitch } from '../components/Toggle.js';
import { FormField } from '../components/Input.js';
import { Select } from '../components/Select.js';
import Toast from '../components/Toast.js';
import { Spinner } from '../components/Spinner.js';
import EmptyState from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';

export default class LanguagesPage {
    constructor(container) {
        this.container = container;
        this.languages = [];
        this.isLoading = false;
        this.modal = null;
        this.availableLanguages = Array.isArray(window.mpzLanguages?.availableLanguages)
            ? window.mpzLanguages.availableLanguages
            : [];

        this.defaultCountryByLanguage = (window.mpzLanguages && typeof window.mpzLanguages.defaultCountryByLanguage === 'object')
            ? window.mpzLanguages.defaultCountryByLanguage
            : {};
        this.sortField = 'sortOrder';
        this.sortDirection = 'asc';

        // Bind methods
        this.render = this.render.bind(this);
        this.loadLanguages = this.loadLanguages.bind(this);
        this.showAddModal = this.showAddModal.bind(this);
        this.showEditModal = this.showEditModal.bind(this);
        this.deleteLanguage = this.deleteLanguage.bind(this);
        this.saveLanguage = this.saveLanguage.bind(this);
        this.toggleLanguageStatus = this.toggleLanguageStatus.bind(this);
        this.setDefaultLanguage = this.setDefaultLanguage.bind(this);
    }

    /**
     * Initialize and render the page
     */
    async render() {
        clear(this.container);

        const header = PageHeader({
            title: __('Languages', 'multilingual-press-zone'),
            description: __('Manage available languages for your multilingual site', 'multilingual-press-zone'),
            actions: [
                Button({
                    label: __('Add Language', 'multilingual-press-zone'),
                    variant: 'primary',
                    icon: '+',
                    onClick: this.showAddModal
                })
            ]
        });

        // Languages table container
        this.tableContainer = el('div', { class: 'mpz-languages-table-container' });

        this.container.appendChild(header);
        this.container.appendChild(this.tableContainer);

        // Load languages
        await this.loadLanguages();
    }

    /**
     * Load languages from REST API
     */
    async loadLanguages() {
        this.isLoading = true;
        this.renderTable();

        try {
            const data = await API.get('/languages');
            // REST controller returns { success: true, data: [...] }
            this.languages = data.data || data.languages || [];
            this.isLoading = false;
            this.renderTable();
        } catch (error) {
            Toast.error(error?.message || __('Failed to load languages', 'multilingual-press-zone'));
            this.isLoading = false;
            this.renderTable();
        }
    }

    /**
     * Render the languages table
     */
    renderTable() {
        clear(this.tableContainer);

        // Loading state
        if (this.isLoading) {
            this.tableContainer.appendChild(
                el('div', { class: 'presszone-multilingual-loading' },
                    Spinner({ size: 'md', label: __('Loading languages...', 'multilingual-press-zone') }),
                    el('p', {}, __('Loading languages...', 'multilingual-press-zone'))
                )
            );
            return;
        }

        // Empty state
        if (this.languages.length === 0) {
            this.tableContainer.appendChild(
                EmptyState(
                    '🌐',
                    __('No languages configured yet.', 'multilingual-press-zone'),
                    __('Add your first language to start translating content.', 'multilingual-press-zone'),
                    Button({
                        label: __('Add Your First Language', 'multilingual-press-zone'),
                        variant: 'primary',
                        onClick: this.showAddModal
                    })
                )
            );
            return;
        }

        // Sort languages
        const sortedLanguages = this.sortLanguages(this.languages);

        // Build table
        const table = el('table', { class: 'mpz-languages-table' },
            // Header
            el('thead', {},
                el('tr', {},
                    el('th', { class: 'mpz-col-flag' }, __('Flag', 'multilingual-press-zone')),
                    el('th', { class: 'mpz-col-name' },
                        el('button', {
                            class: 'mpz-sort-button',
                            onclick: () => this.setSortField('name')
                        },
                            __('Name', 'multilingual-press-zone'),
                            this.sortField === 'name' ? (this.sortDirection === 'asc' ? ' ▲' : ' ▼') : ''
                        )
                    ),
                    el('th', { class: 'mpz-col-code' }, __('Code', 'multilingual-press-zone')),
                    el('th', { class: 'mpz-col-locale' }, __('Locale', 'multilingual-press-zone')),
                    el('th', { class: 'mpz-col-direction' }, __('Direction', 'multilingual-press-zone')),
                    el('th', { class: 'mpz-col-status' }, __('Status', 'multilingual-press-zone')),
                    el('th', { class: 'mpz-col-actions' }, __('Actions', 'multilingual-press-zone'))
                )
            ),
            // Body
            el('tbody', {},
                ...sortedLanguages.map(lang => this.renderLanguageRow(lang))
            )
        );

        this.tableContainer.appendChild(table);
    }

    /**
     * Render a single language row
     */
    renderLanguageRow(lang) {
        return el('tr', {
            class: lang.isActive ? '' : 'mpz-inactive',
            'data-language-id': lang.id
        },
            // Flag
            el('td', { class: 'mpz-col-flag' },
                this.renderFlag(this.getDisplayFlagCode(lang), lang.code)
            ),
            // Name (with native name)
            el('td', { class: 'mpz-col-name' },
                el('strong', {}, lang.name),
                el('br'),
                el('span', { class: 'mpz-native-name' }, lang.nativeName),
                lang.isDefault ? el('span', { class: 'mpz-badge mpz-badge-default' }, __('Default', 'multilingual-press-zone')) : null
            ),
            // Code
            el('td', { class: 'mpz-col-code' }, lang.code),
            // Locale
            el('td', { class: 'mpz-col-locale' }, lang.locale),
            // Direction
            el('td', { class: 'mpz-col-direction' },
                el('span', { class: 'mpz-direction mpz-direction-' + lang.textDirection },
                    String(lang.textDirection || '').toUpperCase()
                )
            ),
            // Status (Toggle)
            el('td', { class: 'mpz-col-status' },
                ToggleSwitch({
                    checked: !!lang.isActive,
                    label: __('Toggle language status', 'multilingual-press-zone'),
                    disabled: !!lang.isDefault,
                    optimistic: false,
                    onChange: (newValue) => this.toggleLanguageStatus(lang, newValue)
                })
            ),
            // Actions
            el('td', { class: 'mpz-col-actions' },
                el('div', { class: 'mpz-actions-group' },
                    !lang.isDefault ? Button({
                        label: __('Set Default', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        onClick: () => this.setDefaultLanguage(lang.code)
                    }) : null,
                    Button({
                        label: __('Edit', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        onClick: () => this.showEditModal(lang.id)
                    }),
                    !lang.isDefault ? Button({
                        label: __('Delete', 'multilingual-press-zone'),
                        variant: 'danger',
                        size: 'sm',
                        onClick: () => this.deleteLanguage(lang.id)
                    }) : null
                )
            )
        );
    }

    /**
     * Render flag emoji
     */
    renderFlag(flagCode, langCode) {
        // Map language codes to country codes for flag display
        const langToFlagMap = {
            'en': 'US',
            'es': 'ES',
            'fr': 'FR',
            'de': 'DE',
            'it': 'IT',
            'pt': 'PT',
            'kk': 'KZ',
            'ru': 'RU',
            'zh': 'CN',
            'ja': 'JP',
            'ko': 'KR',
            'ar': 'SA',
            'he': 'IL',
            'nl': 'NL',
            'pl': 'PL',
            'sv': 'SE',
            'da': 'DK',
            'fi': 'FI',
            'no': 'NO',
            'cs': 'CZ',
            'hu': 'HU',
            'ro': 'RO',
            'tr': 'TR',
            'el': 'GR',
            'th': 'TH',
            'vi': 'VN',
            'id': 'ID',
            'ms': 'MY',
            'uk': 'UA',
            'bg': 'BG',
            'hr': 'HR',
            'sk': 'SK',
            'sl': 'SI',
            'sr': 'RS',
            'et': 'EE',
            'lv': 'LV',
            'lt': 'LT',
            'fa': 'IR',
            'hi': 'IN',
            'bn': 'BD'
        };

        // Use provided flag code or look up from language code
        const code = flagCode || (langCode && langToFlagMap[langCode.toLowerCase()]);

        if (!code) return '-';

        // Convert country code to flag emoji (Unicode regional indicator symbols)
        const codePoints = [...code.toUpperCase()].map(char =>
            0x1F1E6 + char.charCodeAt(0) - 65
        );
        return String.fromCodePoint(...codePoints);
    }

    /**
     * Determine which flag code to display.
     *
     * This keeps UI consistent even if older rows were created with fallback `*_US` locales.
     */
    getDisplayFlagCode(lang) {
        const code = String(lang?.code || '').toLowerCase().trim();
        const current = String(lang?.flagCode || '').toUpperCase().trim();
        const locale = String(lang?.locale || '').trim().replace('-', '_');

        if (!/^[a-z]{2}$/.test(code)) {
            return current || '';
        }

        // Never override English. 'en' legitimately defaults to US in our UI.
        if (code === 'en') {
            return current || 'US';
        }

        const suggested = String(this.defaultCountryByLanguage?.[code] || '').toUpperCase().trim();
        const isFallbackUsLocale = locale === `${code}_US`;

        // If a row looks like it was created via fallback (xx_US + US flag), display the suggested default.
        if (suggested && /^[A-Z]{2}$/.test(suggested)) {
            if ((current === 'US' || !current) && isFallbackUsLocale) {
                return suggested;
            }

            // If the stored flag is missing, prefer the suggested default.
            if (!current) {
                return suggested;
            }
        }

        return current;
    }

    /**
     * Sort languages
     */
    sortLanguages(languages) {
        const sorted = [...languages].sort((a, b) => {
            let aVal, bVal;

            switch (this.sortField) {
                case 'name':
                    aVal = a.name.toLowerCase();
                    bVal = b.name.toLowerCase();
                    break;
                case 'sortOrder':
                default:
                    aVal = a.sortOrder || 0;
                    bVal = b.sortOrder || 0;
                    break;
            }

            if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }

    /**
     * Set sort field and direction
     */
    setSortField(field) {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }
        this.renderTable();
    }

    /**
     * Show Add Language modal
     */
    showAddModal() {
        this.modal = new Modal({
            title: __('Add Language', 'multilingual-press-zone'),
            width: '600px'
        });

        const formData = {
            languagePreset: '',
            name: '',
            nativeName: '',
            code: '',
            locale: '',
            flagCode: '',
            textDirection: 'ltr',
            isActive: true
        };

        const form = this.renderLanguageForm(formData, false);

        this.modal.render(
            form,
            [
                Button({
                    label: __('Cancel', 'multilingual-press-zone'),
                    variant: 'ghost',
                    onClick: () => this.modal.close()
                }),
                Button({
                    label: __('Add Language', 'multilingual-press-zone'),
                    variant: 'primary',
                    onClick: () => this.saveLanguage(formData, false)
                })
            ]
        );

        this.modal.open();
    }

    /**
     * Show Edit Language modal
     */
    showEditModal(languageId) {
        const language = this.languages.find(l => l.id === languageId);
        if (!language) {
            Toast.error(__('Language not found', 'multilingual-press-zone'));
            return;
        }

        this.modal = new Modal({
            title: __('Edit Language', 'multilingual-press-zone'),
            width: '600px'
        });

        const formData = { ...language };
        const form = this.renderLanguageForm(formData, true);

        this.modal.render(
            form,
            [
                Button({
                    label: __('Cancel', 'multilingual-press-zone'),
                    variant: 'ghost',
                    onClick: () => this.modal.close()
                }),
                Button({
                    label: __('Save Changes', 'multilingual-press-zone'),
                    variant: 'primary',
                    onClick: () => this.saveLanguage(formData, true)
                })
            ]
        );

        this.modal.open();
    }

    /**
     * Render language form
     */
    renderLanguageForm(formData, isEdit) {
        const form = el('div', { class: 'mpz-language-form' });

        // Add language preset dropdown for Add modal.
        if (!isEdit) {
            const presetOptions = [
                {
                    value: '',
                    label: __('Select a language...', 'multilingual-press-zone'),
                    default: true
                },
                ...this.availableLanguages
                    .filter(l => l && l.code && l.name)
                    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
                    .map(l => ({
                        value: String(l.code).toLowerCase(),
                        label: `${l.name} (${String(l.code).toLowerCase()})`
                    }))
            ];

            form.appendChild(
                Select(
                    'languagePreset',
                    __('Language Name (English)', 'multilingual-press-zone'),
                    __('Choose from the full language list; fields will auto-fill.', 'multilingual-press-zone'),
                    presetOptions,
                    formData,
                    null,
                    (presetCode) => this.applyLanguagePreset(formData, presetCode, form),
                    { required: true }
                )
            );
        } else {
            // Edit modal keeps a simple text field for the name.
            form.appendChild(
                FormField('name', __('Language Name (English)', 'multilingual-press-zone'), __('e.g., Spanish', 'multilingual-press-zone'), 'text', formData, null, {
                    required: true,
                    placeholder: 'Spanish'
                })
            );
        }

        form.appendChild(
            FormField('nativeName', __('Native Name', 'multilingual-press-zone'), __('e.g., Espanol', 'multilingual-press-zone'), 'text', formData, null, {
                required: true,
                placeholder: 'Espanol'
            })
        );

        form.appendChild(
            FormField('code', __('Language Code', 'multilingual-press-zone'), __('ISO 639-1 (2 letters)', 'multilingual-press-zone'), 'text', formData, null, {
                required: true,
                maxlength: 2,
                pattern: '[a-z]{2}',
                placeholder: 'es',
                disabled: isEdit ? true : undefined
            })
        );

        form.appendChild(
            FormField('locale', __('Locale', 'multilingual-press-zone'), __('e.g., es_ES, en_US', 'multilingual-press-zone'), 'text', formData, null, {
                required: true,
                pattern: '[a-z]{2,3}_[A-Z]{2}',
                placeholder: 'es_ES'
            })
        );

        form.appendChild(
            FormField('flagCode', __('Flag Code', 'multilingual-press-zone'), __('ISO 3166-1 alpha-2 country code', 'multilingual-press-zone'), 'text', formData, null, {
                maxlength: 2,
                pattern: '[A-Z]{2}',
                placeholder: 'ES'
            })
        );

        form.appendChild(
            Select('textDirection', __('Text Direction', 'multilingual-press-zone'), null, [
                { value: 'ltr', label: __('Left-to-Right (LTR)', 'multilingual-press-zone') },
                { value: 'rtl', label: __('Right-to-Left (RTL)', 'multilingual-press-zone') }
            ], formData, null)
        );

        // Use the shared Toggle component for Active.
        form.appendChild(
            Toggle('isActive', __('Active', 'multilingual-press-zone'), '', formData, null)
        );

        return form;
    }

    /**
     * Apply a language preset selection to the form.
     */
    applyLanguagePreset(formData, presetCode, formEl) {
        const code = String(presetCode || '').toLowerCase().trim();
        if (!code) {
            return;
        }

        const preset = this.availableLanguages.find(l => String(l.code || '').toLowerCase() === code);
        if (!preset) {
            return;
        }

        const next = {
            name: String(preset.name || ''),
            nativeName: String(preset.nativeName || ''),
            code: code,
            locale: String(preset.locale || ''),
            flagCode: String(preset.flagCode || ''),
            textDirection: String(preset.textDirection || 'ltr')
        };

        // Update state used for submission.
        formData.name = next.name;
        formData.nativeName = next.nativeName;
        formData.code = next.code;
        formData.locale = next.locale;
        formData.flagCode = next.flagCode;
        formData.textDirection = next.textDirection;

        // Update visible field values (non-reactive form).
        const setFieldValue = (id, value) => {
            const el = formEl.querySelector(`#${id}`);
            if (!el) return;
            el.value = value;
        };

        setFieldValue('nativeName', next.nativeName);
        setFieldValue('code', next.code);
        setFieldValue('locale', next.locale);
        setFieldValue('flagCode', next.flagCode);
        setFieldValue('textDirection', next.textDirection);
    }

    /**
     * Save language (create or update)
     */
    async saveLanguage(formData, isEdit) {
        // If we're adding via preset dropdown, ensure required fields are populated.
        if (!isEdit) {
            const presetCode = String(formData.languagePreset || formData.code || '').toLowerCase().trim();
            if (presetCode) {
                const preset = this.availableLanguages.find(l => String(l.code || '').toLowerCase() === presetCode);
                if (preset) {
                    if (!formData.name) formData.name = String(preset.name || '');
                    if (!formData.nativeName) formData.nativeName = String(preset.nativeName || '');
                    if (!formData.code) formData.code = presetCode;
                    if (!formData.locale) formData.locale = String(preset.locale || '');
                    if (!formData.flagCode) formData.flagCode = String(preset.flagCode || '');
                    if (!formData.textDirection) formData.textDirection = String(preset.textDirection || 'ltr');
                }
            }
        }

        // Basic validation
        if (!formData.name || !formData.nativeName || !formData.code || !formData.locale) {
            Toast.error(__('Please fill in all required fields', 'multilingual-press-zone'));
            return;
        }

        // Validate code format
        if (!/^[a-z]{2}$/.test(formData.code)) {
            Toast.error(__('Language code must be 2 lowercase letters (ISO 639-1)', 'multilingual-press-zone'));
            return;
        }

        // Validate locale format
        if (typeof formData.locale === 'string') {
            formData.locale = formData.locale.trim().replace('-', '_');
        }
        if (!/^[a-z]{2,3}_[A-Z]{2}$/.test(formData.locale)) {
            Toast.error(__('Invalid locale format. Use format like: en_US, es_ES', 'multilingual-press-zone'));
            return;
        }

        // Validate flag code if provided
        if (formData.flagCode && !/^[A-Z]{2}$/.test(formData.flagCode)) {
            Toast.error(__('Flag code must be 2 uppercase letters (ISO 3166-1 alpha-2)', 'multilingual-press-zone'));
            return;
        }

        // Only send the fields the REST API expects.
        const payload = {
            name: formData.name,
            nativeName: formData.nativeName,
            code: formData.code,
            locale: formData.locale,
            textDirection: formData.textDirection,
            isActive: formData.isActive
        };

        // Only send flagCode if non-empty (backend validates ISO code strictly).
        if (typeof formData.flagCode === 'string' && formData.flagCode.trim()) {
            payload.flagCode = formData.flagCode.trim();
        }

        try {
            if (isEdit) {
                await API.patch(`/languages/${formData.code}`, payload);
            } else {
                await API.post('/languages', payload);
            }

            Toast.success(isEdit
                ? __('Language updated successfully', 'multilingual-press-zone')
                : __('Language added successfully', 'multilingual-press-zone')
            );

            this.modal.close();
            await this.loadLanguages();
        } catch (error) {
            Toast.error(error?.message || __('Failed to save language', 'multilingual-press-zone'));
        }
    }

    /**
     * Delete language
     */
    async deleteLanguage(languageId) {
        const language = this.languages.find(l => l.id === languageId);
        if (!language) return;

        if (!confirm(__('Are you sure you want to delete this language? This action cannot be undone.', 'multilingual-press-zone'))) {
            return;
        }

        try {
            // Find language code
            const langCode = language.code;

            await API.delete(`/languages/${langCode}`);

            Toast.success(__('Language deleted successfully', 'multilingual-press-zone'));
            await this.loadLanguages();
        } catch (error) {
            Toast.error(error?.message || __('Failed to delete language', 'multilingual-press-zone'));
        }
    }

    /**
     * Toggle language active status
     */
    async toggleLanguageStatus(language, desiredActive = null) {
        const nextStatus = desiredActive === null ? !language.isActive : !!desiredActive;
        const endpoint = nextStatus ? 'activate' : 'deactivate';

        try {
            await API.post(`/languages/${language.code}/${endpoint}`);

            Toast.success(nextStatus
                ? __('Language activated', 'multilingual-press-zone')
                : __('Language deactivated', 'multilingual-press-zone')
            );

            await this.loadLanguages();
        } catch (error) {
            Toast.error(error?.message || __('Failed to update language status', 'multilingual-press-zone'));
            this.renderTable();
        }
    }

    /**
     * Set default language
     */
    async setDefaultLanguage(languageId) {
        if (!confirm(__('Set this language as the default language for your site?', 'multilingual-press-zone'))) {
            return;
        }

        try {
            await API.post(`/languages/${languageId}/set-default`);

            Toast.success(__('Default language updated', 'multilingual-press-zone'));
            await this.loadLanguages();
        } catch (error) {
            Toast.error(error?.message || __('Failed to set default language', 'multilingual-press-zone'));
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.modal) {
            this.modal.close();
        }
        clear(this.container);
    }
}
